import React, { useEffect, useState } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import { ShieldCheck, Check, X, RotateCcw, Pencil, Loader2, History, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import { coachAPI, mediaUrl } from '../services/api';

const mediaLabel = (mt) => (mt === 'video' ? '🎬 Vidéo' : mt === 'photo' ? '📷 Photo' : '✅ Simple');
const EVENT_LABEL = { validation: 'Validation', adjustment: 'Points corrigés', cancel: 'Annulée', restore: 'Rétablie' };
const EVENT_COLOR = { validation: 'bg-green-100 text-green-700 border-green-200', adjustment: 'bg-orange-100 text-orange-700 border-orange-200', cancel: 'bg-slate-100 text-slate-500 border-slate-200', restore: 'bg-cyan-100 text-cyan-700 border-cyan-200' };

export const CoachProofs = () => {
  const [proofs, setProofs] = useState([]);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // `${uid}-${day}`
  const [editValue, setEditValue] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () => {
    Promise.all([coachAPI.proofs(), coachAPI.proofLog()])
      .then(([p, l]) => { setProofs(p); setLog(l); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const act = async (r, action, points) => {
    setBusy(true);
    try {
      await coachAPI.adjustProof({ user_id: r.user_id, day_number: r.day_number, action, points });
      toast.success('Mise à jour effectuée');
      setEditing(null);
      load();
    } catch { toast.error("Échec de l'opération"); }
    finally { setBusy(false); }
  };

  if (loading) return <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50"><Loader2 className="w-5 h-5 animate-spin text-red-600 mx-auto" /></Card>;

  return (
    <div className="space-y-4" data-testid="coach-proofs">
      <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
        <h3 className="font-bold text-red-950 dark:text-red-50 mb-1 flex items-center gap-2"><ShieldCheck className="w-5 h-5 text-red-600" />Preuves & validation des points</h3>
        <p className="text-xs text-muted-foreground mb-4">Consulte les preuves, corrige les points ou annule une validation en cas d'erreur ou d'abus.</p>
        {proofs.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-3">Aucune preuve pour le moment.</p>
        ) : (
          <div className="space-y-2 max-h-[520px] overflow-y-auto pr-1">
            {proofs.map((r) => {
              const key = `${r.user_id}-${r.day_number}`;
              const cancelled = r.status === 'annule';
              return (
                <div key={key} data-testid={`proof-row-${key}`} className={`flex items-center gap-3 p-2.5 rounded-xl border ${cancelled ? 'border-slate-200 bg-slate-50 dark:bg-slate-900/30 opacity-80' : 'border-border'}`}>
                  <Avatar className="w-9 h-9"><AvatarImage src={mediaUrl(r.user_avatar)} /><AvatarFallback className="bg-red-600 text-white text-xs">{r.user_name?.charAt(0)}</AvatarFallback></Avatar>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-foreground truncate">{r.user_name} <span className="text-muted-foreground font-normal">· Jour {r.day_number}</span></p>
                    <p className="text-xs text-muted-foreground flex items-center gap-2">
                      <span>{mediaLabel(r.media_type)}</span>
                      {r.proof_url && <a href={mediaUrl(r.proof_url)} target="_blank" rel="noreferrer" data-testid={`proof-link-${key}`} className="text-red-600 inline-flex items-center gap-0.5">preuve <ExternalLink className="w-3 h-3" /></a>}
                      {cancelled && <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-[10px]">Annulée</Badge>}
                    </p>
                  </div>
                  {editing === key ? (
                    <div className="flex items-center gap-1 shrink-0">
                      <Input type="number" value={editValue} onChange={(e) => setEditValue(e.target.value)} data-testid={`points-input-${key}`} className="w-20 h-8 rounded-lg text-sm" />
                      <button onClick={() => act(r, 'set_points', parseInt(editValue, 10) || 0)} disabled={busy} data-testid={`points-save-${key}`} className="p-1.5 text-green-600"><Check className="w-4 h-4" /></button>
                      <button onClick={() => setEditing(null)} className="p-1.5 text-muted-foreground"><X className="w-4 h-4" /></button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1 shrink-0">
                      <span className="text-sm font-extrabold text-orange-500 w-14 text-right">{r.points_earned} pts</span>
                      {!cancelled && (
                        <>
                          <button onClick={() => { setEditing(key); setEditValue(String(r.points_earned)); }} title="Corriger les points" data-testid={`edit-points-${key}`} className="p-1.5 text-muted-foreground hover:text-orange-600"><Pencil className="w-4 h-4" /></button>
                          <button onClick={() => act(r, 'cancel')} disabled={busy} title="Annuler la validation" data-testid={`cancel-proof-${key}`} className="p-1.5 text-muted-foreground hover:text-red-600"><X className="w-4 h-4" /></button>
                        </>
                      )}
                      {cancelled && (
                        <button onClick={() => act(r, 'restore')} disabled={busy} title="Rétablir" data-testid={`restore-proof-${key}`} className="p-1.5 text-muted-foreground hover:text-cyan-600"><RotateCcw className="w-4 h-4" /></button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {log.length > 0 && (
        <Card className="p-5 rounded-2xl border-red-100 dark:border-red-900/50">
          <h3 className="font-bold text-red-950 dark:text-red-50 mb-3 flex items-center gap-2"><History className="w-5 h-5 text-red-600" />Journal des preuves</h3>
          <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
            {log.map((e) => (
              <div key={e.id} className="flex items-center gap-3 text-sm" data-testid={`log-${e.id}`}>
                <Badge className={`text-[10px] shrink-0 ${EVENT_COLOR[e.event] || 'bg-muted'}`}>{EVENT_LABEL[e.event] || e.event}</Badge>
                <span className="flex-1 min-w-0 truncate text-foreground">{e.user_name} · Jour {e.day_number} · {mediaLabel(e.media_type)} · {e.points_earned} pts{e.note ? ` — ${e.note}` : ''}</span>
                <span className="text-xs text-muted-foreground shrink-0">{(e.created_at || '').slice(0, 10)}</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
};
